import { h, resolveComponent } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { ActiveUser } from "@/services/user";
import { IS_MZ_BRANDING } from "@/constants/config";

// Containers
const TheContainer = () => import("@/containers/TheContainer");
const AccountContainer = () => import("@/containers/AccountContainer");

// Tickets
const TicketsList = () => import("@/views/tickets/TicketsList");
const TicketsCreate = () => import("@/views/tickets/TicketsCreate");

// Members
const MembersList = () => import("@/views/members/MembersList");
const MembersCreate = () => import("@/views/members/MembersCreate");

// Labels
const LabelManagement = () => import("@/views/label_management/LabelManagement");

// Entity
const EntityList = () => import("@/views/entity/EntityList");
const EntityForms = () => import("@/views/entity/EntityForms");
const EntityFormEdit = () => import("@/views/entity/EntityFormEdit");
const EntityDetails = () => import("@/views/entity/EntityDetails");
const CycleDetails = () => import("@/views/entity/CycleDetails");

// Quarter
const QuarterList = () => import("@/views/quarter/QuarterList");
const QuarterDetails = () => import("@/views/quarter/QuarterDetails");

// Views - Pages
const Page404 = () => import("@/views/pages/Page404");
const Login = () => import("@/views/pages/Login");
const ForgotPassword = () => import("@/views/pages/ForgotPassword");
const ResetPassword = () => import("@/views/pages/ResetPassword");

import store from './../store';

const router = createRouter({
  history: createWebHistory(),
  linkActiveClass: 'active',
  routes: configRoutes()
});

const isOpenAccess = (route) =>
  route.matched.some((route) => route.meta?.isOpenAccess);

const isFound = (route) => route.matched[0].name !== "NotFound";

router.beforeEach((to, from, next) => {
  if (to.meta?.getTitle) to.meta.label = to.meta.getTitle(to.params, to.query);
  const isAuthenticated = ActiveUser.get();
  if (!isAuthenticated && !isOpenAccess(to)) {
    if (isFound(to)) {
      localStorage.setItem("pathToLoadAfterLogin", to.path);
    }
    return next({ name: IS_MZ_BRANDING ? "Login" : "Management Login" });
  }
  return next();
});
export default router;

function configRoutes() {

  return [
    {
      path: "/",
      redirect: "/list-of-tasks",
      meta: {
        hideInBreadcrumbs: true,
      },
      component: TheContainer,
      children: [
        {
          path: "/list-of-tasks",
          meta: {
            label: "List of tasks",
          },
          component: {
            render() {
              return h(resolveComponent("router-view"));
            },
          },
          children: [
            {
              path: "",
              name: "List of tasks",
              component: TicketsList,
            },
            {
              path: ":id",
              name: "Create task",
              component: TicketsCreate,
              meta: {
                getTitle: (params) =>
                  params.id === 'create' ? 'Ticket creation' : `Ticket number ${params.id}`,
              }
            },
          ],
        },
        {
          path: "/members",
          meta: {
            label: "Members",
          },
          component: {
            render() {
              return h(resolveComponent("router-view"));
            },
          },
          children: [
            {
              path: "",
              name: "Members",
              component: MembersList,
            },
            {
              path: ":id",
              name: "Create member",
              component: MembersCreate,
            },
          ],
        },
        {
          path: "/label-management",
          name: "Label Management",
          component: LabelManagement,
        },
        {
          path: "/entity-list",
          meta: {
            label: "Entity List",
          },
          component: {
            render() {
              return h(resolveComponent("router-view"));
            },
          },
          children: [
            {
              path: "",
              name: "Entity List",
              component: EntityList,
            },
          ],
        },
        {
          path: "/entity-id-form",
          meta: {
            hideInBreadcrumbs: true,
          },
          component: {
            render() {
              return h(resolveComponent("router-view"));
            },
          },
          children: [
            {
              name: "Entity Id Form",
              path: "",
              component: EntityForms,
            },
            {
              name: "EntityFormEdit",
              path: ":id",
              component: EntityFormEdit,
              meta: {
                paramKey: "entityName",
                getTitle: (params, query) => {
                  return params.id === 'create' ? 'Create Form' : `${!!query.preview ? "Preview" : "Edit"} Form ${params.id}`
                }
              }
            }
          ]
        },
        {
          path: "/closing-file",
          meta: {
            label: "Closing File",
          },
          component: {
            render() {
              return h(resolveComponent("router-view"));
            },
          },
          children: [
            {
              path: "",
              name: "Closing File",
              component: QuarterList,
            },
            {
              path: ":quarter_name",
              name: "Quarter Details",
              meta: {
                paramKey: "quarter_name",
                getTitle: (params) => store.state?.quarter?.display_name
              },
              component: {
                render() {
                  return h(resolveComponent("router-view"));
                },
              },
              children: [
                {
                  path: "",
                  name: "Quarter View",
                  component: QuarterDetails,
                  meta: {
                    getTitle: (params) => store.state?.quarter?.display_name
                  },
                },
                {
                  path: ":entity_id",
                  name: "Quarter Entity",
                  meta: {
                    paramKey: "entity_id"
                  },
                  component: {
                    render() {
                      return h(resolveComponent("router-view"));
                    },
                  },
                  children: [
                    {
                      name: "Entity Details",
                      path: "",
                      component: EntityDetails,
                    },
                    {
                      name: "Cycle Details",
                      path: ":cycle",
                      meta: {
                        paramKey: "cycleName",
                      },
                      component: CycleDetails,
                    },
                  ]
                }
              ]
            }
          ]
        }
      ],
    },
    {
      path: "/oauth",
      meta: {
        isOpenAccess: true,
      },
      component: AccountContainer,
      children: [
        {
          path: "",
          name: "OAuth Login",
          component: Login,
        },
      ],
    },
    {
      path: "/account",
      name: "account",
      meta: {
        isOpenAccess: true,
      },
      component: AccountContainer,
      children: [
        {
          path: "login",
          name: "Login",
          component: Login,
        },
        {
          path: "forgot",
          name: "Forgot password",
          component: ForgotPassword,
        },
        {
          path: "reset/:token",
          name: "Password reset",
          component: ResetPassword,
        },
        {
          path: "login_management",
          name: "Management Login",
          component: Login,
          props: {
            show_email_login: true,
          },
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      component: TheContainer,
      children: [
        {
          path: '',
          name: 'NotFound',
          component: Page404,
        },
      ]
    }
  ];
}
